import { useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores';
import { useActiveTams } from '../../hooks/useActiveTams';
import { useAnnualTamPrimes } from '../../hooks/useAnnualTamPrimes';
import { usePrimesBreadcrumbContext } from '../../hooks/usePrimesBreadcrumbContext';
import { errorLogger } from '../../helpers/error';
import {
  getAnnualCurrentMonthHeaderState,
  getAnnualDealsGoalState,
  getAnnualGoalHeaderState,
  mapAnnualTamPrimeToTableRows,
} from '../../utils/annualPrime.utils';
import { getTamDisplayName, formatPrimeEuro } from '../../utils/monthlyPrime.utils';
import { mapLifetimeMarginCumulToSubtitle } from '../../utils/lifetimeMargin.utils';
import { useLifetimeMarginCumul } from '../../hooks/useLifetimeMarginCumul';
import AnnualTamPrimesHeader from '../molecules/AnnualTamPrimesHeader';
import AnnualTamPrimesTable from '../molecules/AnnualTamPrimesTable';

export type AnnualTamPrimesProps = {
  tamEmail?: string;
  referenceDate?: Date;
};

const AnnualTamPrimes = ({
  tamEmail: tamEmailOverride,
  referenceDate: referenceDateOverride,
}: AnnualTamPrimesProps) => {
  const { t } = useTranslation(['primes']);
  const auth = useAuthStore((state) => state.auth);
  const { getTamDisplayName: getActiveTamDisplayName, getTamByEmail } = useActiveTams();
  const authTamEmail = typeof auth?.user?.email === 'string' ? auth.user.email.trim() : '';
  const isTamPreview = Boolean(tamEmailOverride);
  const tamEmail = isTamPreview ? tamEmailOverride?.trim() : authTamEmail;
  const tamDisplayName = isTamPreview ? getActiveTamDisplayName(tamEmail ?? '') : getTamDisplayName(auth?.user);
  const tamAvatarUrl = tamEmail
    ? getTamByEmail(tamEmail)?.avatarUrl ?? (isTamPreview ? null : auth?.user?.picture) ?? null
    : null;
  const initialYear = referenceDateOverride ? format(referenceDateOverride, 'yyyy') : format(new Date(), 'yyyy');
  const [selectedYear, setSelectedYear] = useState(() => initialYear);

  const { loading, error, aggregate } = useAnnualTamPrimes(tamEmail, selectedYear);
  const { cumul: lifetimeCumul, loading: lifetimeLoading, error: lifetimeError } = useLifetimeMarginCumul(tamEmail);
  const breadcrumbContext = useMemo(
    () => ({
      tamDisplayName: isTamPreview ? tamDisplayName : undefined,
      tamEmail: isTamPreview ? tamEmail : undefined,
      selectedYear,
    }),
    [isTamPreview, tamDisplayName, tamEmail, selectedYear],
  );
  usePrimesBreadcrumbContext(breadcrumbContext);
  const tableRows = useMemo(() => (aggregate ? mapAnnualTamPrimeToTableRows(aggregate) : []), [aggregate]);
  const currentMonth = useMemo(() => (aggregate ? getAnnualCurrentMonthHeaderState(aggregate) : null), [aggregate]);
  const goalState = useMemo(() => (aggregate ? getAnnualGoalHeaderState(aggregate) : null), [aggregate]);
  const dealsState = useMemo(
    () => (aggregate ? getAnnualDealsGoalState(aggregate, selectedYear) : null),
    [aggregate, selectedYear],
  );

  if (!tamEmail) {
    return <ErrorMessage variant="body1">{t('missingTamEmail')}</ErrorMessage>;
  }

  const fatalError = error ?? lifetimeError;
  if (fatalError) {
    errorLogger(fatalError, { extraInfos: 'getTamPrimes or getLifetimeMarginCumuls failed on /primes/annuel' });
    return <ErrorMessage variant="body1">{t('annualLoadError')}</ErrorMessage>;
  }

  return (
    <Container>
      <AnnualTamPrimesHeader
        tamDisplayName={tamDisplayName}
        tamEmail={tamEmail}
        tamSeniority={aggregate?.tamSeniority}
        tamAvatarUrl={tamAvatarUrl}
        selectedYear={selectedYear}
        onSelectedYearChange={setSelectedYear}
        currentMonth={currentMonth}
        goalState={goalState}
        dealsState={dealsState}
        isTamPreview={isTamPreview}
        loading={loading}
        lifetimeMarginAmount={formatPrimeEuro(lifetimeCumul?.totalMargin ?? 0)}
        lifetimeMarginSubtitle={mapLifetimeMarginCumulToSubtitle(lifetimeCumul)}
      />
      <AnnualTamPrimesTable
        rows={tableRows}
        loading={loading}
        annualAggregate={aggregate}
        tamEmail={tamEmail}
        isTamPreview={isTamPreview}
      />
    </Container>
  );
};

export default AnnualTamPrimes;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  color: theme.palette.error.main,
}));
